import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  Field,
  Input,
  Page,
  SparkleIcon,
  Stack,
  TextArea,
  Toggle,
} from '@integra/ui';
import { useApplyCampaignDraft, useDraftCampaign } from '../api/hooks.js';
import type { CampaignDraft } from '../api/types.js';

/**
 * AI campaign assistant (§20.5, AI layer). Describe a goal and pick channels;
 * INTEGRA drafts a campaign — a name, a hero message and per-channel copy — for a
 * human to review. Nothing publishes automatically: applying a draft creates a
 * DRAFT asset you take through the normal governed flow. AI-authored and disclosed.
 */

const CHANNELS = ['LinkedIn', 'X', 'Instagram', 'Facebook', 'TikTok', 'Email newsletter'];

export function AiAssistantPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const draft = useDraftCampaign(workspaceId);
  const apply = useApplyCampaignDraft(workspaceId);

  const [goal, setGoal] = useState('');
  const [audience, setAudience] = useState('');
  const [channels, setChannels] = useState<Set<string>>(new Set(['LinkedIn', 'X']));
  const [result, setResult] = useState<CampaignDraft | null>(null);

  function toggleChannel(channel: string): void {
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) next.delete(channel);
      else next.add(channel);
      return next;
    });
  }

  function submit(): void {
    if (goal.trim().length < 10 || channels.size === 0) return;
    draft.mutate(
      { goal: goal.trim(), audience: audience.trim() || null, channels: [...channels] },
      { onSuccess: (res) => setResult(res.data) },
    );
  }

  function applyDraft(): void {
    if (result === null) return;
    apply.mutate(
      { name: result.name, heroTitle: result.heroTitle, heroBody: result.heroBody },
      { onSuccess: (res) => navigate(`/w/${workspaceId}/assets/${res.data.assetId}`) },
    );
  }

  return (
    <Page
      title="AI campaign assistant"
      icon={SparkleIcon}
      description="Turn a brief into a drafted campaign — a hero message and channel-native copy — for you to review. Nothing publishes on its own."
    >
      <Stack gap="lg">
        <Card title="Your brief" subtitle="Describe the goal; pick the channels to draft for.">
          <Stack gap="md">
            <Field label="Goal / brief" htmlFor="ai-goal">
              <TextArea
                id="ai-goal"
                rows={4}
                value={goal}
                placeholder="Announce our new 30-hour power bank to commuters — emphasise the honest, tested battery life."
                onChange={(e) => setGoal(e.target.value)}
              />
            </Field>
            <Field label="Audience (optional)" htmlFor="ai-audience">
              <Input
                id="ai-audience"
                value={audience}
                placeholder="Daily commuters, 25–45"
                onChange={(e) => setAudience(e.target.value)}
              />
            </Field>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Channels
              </p>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
                {CHANNELS.map((channel) => (
                  <Cluster key={channel} justify="between" align="center">
                    <span className="text-sm text-navy-700">{channel}</span>
                    <Toggle
                      label={channel}
                      checked={channels.has(channel)}
                      onChange={() => toggleChannel(channel)}
                    />
                  </Cluster>
                ))}
              </div>
            </div>
            <Cluster justify="end">
              <Button
                loading={draft.isPending}
                disabled={goal.trim().length < 10 || channels.size === 0}
                onClick={submit}
              >
                Draft the campaign
              </Button>
            </Cluster>
            {draft.error instanceof Error && (
              <Alert tone="warning" title="Could not draft the campaign">
                {draft.error.message}
              </Alert>
            )}
          </Stack>
        </Card>

        {result !== null && (
          <Card
            title={result.name}
            subtitle="A drafted campaign for your review — edit anything before you use it."
            actions={
              <Button loading={apply.isPending} onClick={applyDraft}>
                Create draft asset
              </Button>
            }
          >
            <Stack gap="md">
              <Alert tone="info" title="AI-authored draft">
                {result.disclosure}
              </Alert>
              <div className="rounded-md border border-edge bg-surface-sunken px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                  Hero message
                </p>
                <p className="mt-1 text-sm font-semibold text-navy-700">{result.heroTitle}</p>
                <p className="mt-0.5 text-sm text-ink-muted">{result.heroBody}</p>
              </div>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {result.channels.map((ch) => (
                  <div key={ch.channel} className="rounded-md border border-edge bg-surface p-3">
                    <Badge tone="gold">{ch.channel}</Badge>
                    <p className="mt-2 text-sm font-semibold text-navy-700">{ch.headline}</p>
                    <p className="mt-1 text-sm text-ink-muted">{ch.body}</p>
                  </div>
                ))}
              </div>
              {apply.error instanceof Error && (
                <Alert tone="error" title="Could not create the draft asset">
                  {apply.error.message}
                </Alert>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </Page>
  );
}
